import { alpha, Box, Stack, Tooltip, Typography } from "@mui/material";
import { type FC, memo } from "react";
import { BRAND_PRIMARY } from "@/theme/theme";
import type { DailyStatus, DailyStatusType } from "../../types";
import { clickableProps } from "../../utils/keyboard";

const STATUS_COLORS: Record<DailyStatusType, string> = {
  operational: "var(--mui-palette-success-main)",
  degraded: "var(--mui-palette-warning-main)",
  outage: "var(--mui-palette-error-main)",
  no_data: "var(--mui-palette-action-disabledBackground)",
};

interface UptimeBarProps {
  dailyStatuses: DailyStatus[];
  showLabels?: boolean;
  selectedDate?: string;
  onDayClick?: (status: DailyStatus) => void;
}

export const UptimeBar: FC<UptimeBarProps> = memo(
  ({ dailyStatuses, showLabels = true, selectedDate, onDayClick }) => {
    const formatDate = (dateStr: string) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    };

    const getTooltipContent = (status: DailyStatus) => {
      const statusLabel =
        status.status.charAt(0).toUpperCase() + status.status.slice(1).replace("_", " ");
      const date = formatDate(status.date);

      if (status.status === "no_data") {
        return `${date}: No data`;
      }

      return (
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="caption" display="block" fontWeight={600}>
            {date}
          </Typography>
          <Typography variant="caption" display="block">
            {statusLabel}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            {status.passed_runs}/{status.total_runs} passed
          </Typography>
          {status.total_runs > 0 && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.7 }}>
              Click to view runs
            </Typography>
          )}
        </Box>
      );
    };

    return (
      <Stack spacing={0.5} sx={{ width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            gap: "2px",
            height: "34px",
            width: "100%",
            p: 0.5,
            borderRadius: 1,
            bgcolor: "action.hover",
          }}
        >
          {dailyStatuses.map((status, index) => {
            const isSelected = selectedDate === status.date;
            const hasRuns = status.total_runs > 0;

            return (
              <Tooltip key={index} title={getTooltipContent(status)} arrow placement="top">
                <Box
                  {...(hasRuns && onDayClick ? clickableProps(() => onDayClick(status)) : {})}
                  onClick={() => hasRuns && onDayClick?.(status)}
                  sx={{
                    flex: 1,
                    minWidth: "4px",
                    maxWidth: "10px",
                    borderRadius: "3px",
                    backgroundColor: STATUS_COLORS[status.status],
                    cursor: hasRuns ? "pointer" : "default",
                    transition: "all 0.15s ease",
                    border: isSelected ? `2px solid ${BRAND_PRIMARY}` : "none",
                    boxShadow: isSelected ? `0 0 0 2px ${alpha(BRAND_PRIMARY, 0.3)}` : "none",
                    "&:hover": hasRuns
                      ? {
                          opacity: 0.85,
                          transform: "scaleY(1.2)",
                        }
                      : {},
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>
        {showLabels && dailyStatuses.length > 0 && (
          <Stack direction="row" justifyContent="space-between" sx={{ px: 0.5 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
              {formatDate(dailyStatuses[0].date)}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
              Today
            </Typography>
          </Stack>
        )}
      </Stack>
    );
  },
);

UptimeBar.displayName = "UptimeBar";
